import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MaskLines } from "../components/Reveal";
import SaveButton from "../components/SaveButton";
import { useAuth } from "../context/AuthContext";
import { useAudio } from "../context/AudioContext";
import { api, asList } from "../lib/api";
import useSeo from "../lib/useSeo";

export default function Saved() {
  useSeo("Saved", "Your saved pieces and tracks.");
  const { user, loading, login, saves } = useAuth();
  const { tracks, toggle } = useAudio();
  const [pieces, setPieces] = useState([]);
  useEffect(() => { api.get("/pieces").then((r) => setPieces(asList(r))).catch(() => setPieces([])); }, []);

  if (loading) return <div className="min-h-screen" />;
  if (!user) {
    return (
      <section className="px-5 sm:px-8 lg:px-12 pt-40 pb-32 min-h-[70vh]" data-testid="saved-signin">
        <h1 className="f-display font-black uppercase text-6xl sm:text-8xl leading-[0.85]">Saved</h1>
        <p className="f-serif italic text-2xl mt-6 text-[#cfcec8]">Sign in to keep the pieces that stayed with you.</p>
        <button onClick={login} className="mt-10 f-mono text-xs uppercase tracking-[0.2em] border border-[var(--bone)] px-6 py-3 hover:bg-[var(--bone)] hover:text-[var(--ink)] transition-colors" data-testid="saved-login-button">Sign in with Google</button>
      </section>
    );
  }

  const sp = saves.filter((s) => s.kind === "piece").map((s) => pieces.find((p) => p.slug === s.slug)).filter(Boolean);
  const st = saves.filter((s) => s.kind === "track").map((s) => tracks.find((t) => t.slug === s.slug)).filter(Boolean);

  return (
    <section className="px-5 sm:px-8 lg:px-12 pt-32 pb-24" data-testid="saved-page">
      <p className="eyebrow mb-5">Your coordinates</p>
      <h1 className="f-display font-black uppercase text-[18vw] sm:text-[12vw] lg:text-[9vw] leading-[0.82]"><MaskLines onMount lines={["Saved"]} /></h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mt-16">
        <div>
          <p className="eyebrow mb-4">Writing ({sp.length})</p>
          <ul className="border-t border-[var(--bone)]">
            {sp.map((p) => (
              <li key={p.slug} className="border-b border-[var(--line)] py-5 flex justify-between gap-4 items-baseline">
                <Link to={`/explore/${p.slug}`} className="f-display font-bold uppercase text-3xl leading-none hover:text-[var(--signal)]" data-testid={`saved-piece-${p.slug}`}>{p.title}</Link>
                <SaveButton kind="piece" slug={p.slug} label={false} />
              </li>
            ))}
            {!sp.length && <li className="py-5 f-serif italic text-[#bdbcb5]">Nothing yet. <Link to="/explore" className="link-u">Explore</Link>.</li>}
          </ul>
        </div>
        <div>
          <p className="eyebrow mb-4">Audio ({st.length})</p>
          <ul className="border-t border-[var(--bone)]">
            {st.map((t) => (
              <li key={t.slug} className="border-b border-[var(--line)] py-5 flex justify-between gap-4 items-baseline">
                <button onClick={() => toggle(t)} className="text-left" data-testid={`saved-track-${t.slug}`}>
                  <span className="block f-display font-bold uppercase text-3xl leading-none hover:text-[var(--signal)]">{t.title}</span>
                  <span className="block f-serif italic text-[#bdbcb5]">{t.version}</span>
                </button>
                <SaveButton kind="track" slug={t.slug} label={false} />
              </li>
            ))}
            {!st.length && <li className="py-5 f-serif italic text-[#bdbcb5]">Nothing yet. <Link to="/listen" className="link-u">Listen</Link>.</li>}
          </ul>
        </div>
      </div>
    </section>
  );
}
